// src/pages/OAuthCallback.tsx
import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { functions } from "src/api/firebase";
import { httpsCallable } from "firebase/functions";
import { Alert, AlertTitle, AlertDescription } from "src/components/ui/Alert";
import { Button } from "src/components/ui/Button";
import { getErrorMessage } from "src/utils/getErrorMessage";

interface OAuthCallbackProps {}

const OAuthCallback: React.FC<OAuthCallbackProps> = () => {
    const location = useLocation();
    const [successMsg, setSuccessMsg] = useState<string>("");
    const [errorMsg, setErrorMsg] = useState<string>("");
    const autoCloseTimer = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const code = params.get("code");
        const error = params.get("error");
        const state = params.get("state");

        if (error) {
            setErrorMsg("Google OAuth failed or was denied. Please try again.");
            return;
        }
        if (!code) {
            setErrorMsg(
                "Invalid OAuth callback. No authorization code provided."
            );
            return;
        }

        const linkAccount = httpsCallable(functions, "linkGoogleAccount");
        linkAccount({ code, state })
            .then((result) => {
                const linkedEmail = (result.data as any)?.email;
                const msg = linkedEmail
                    ? `Successfully linked Gmail account: ${linkedEmail}`
                    : "Gmail account linked successfully!";
                setSuccessMsg(msg);
                // Set up an auto-close timer for 3 seconds.
                autoCloseTimer.current = setTimeout(() => {
                    handleClose();
                }, 3000);
            })
            .catch((err) => {
                console.error("Error linking Gmail account:", err);
                setErrorMsg(
                    "Failed to link Gmail account: " + getErrorMessage(err)
                );
            });
    }, [location.search]);

    // Clear timer if component unmounts
    useEffect(() => {
        return () => {
            if (autoCloseTimer.current) {
                clearTimeout(autoCloseTimer.current);
            }
        };
    }, []);

    const handleClose = () => {
        // Clear the timer if user manually closes before auto-close fires.
        if (autoCloseTimer.current) {
            clearTimeout(autoCloseTimer.current);
            autoCloseTimer.current = null;
        }
        console.log("??, closing window");
        window.close();
        // if (window.opener) {
        //     console.log("??, closing window");
        //     window.opener.focus();
        //     window.close();
        // } else {
        //     // navigate("accounts");
        // }
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-screen p-6 space-y-6">
            {errorMsg && (
                <Alert variant="destructive">
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{errorMsg}</AlertDescription>
                </Alert>
            )}
            {successMsg && (
                <Alert variant="default">
                    <AlertTitle>Success</AlertTitle>
                    <AlertDescription>{successMsg}</AlertDescription>
                </Alert>
            )}
            {!errorMsg && !successMsg && (
                <Alert variant="default">
                    <AlertTitle>Linking Account...</AlertTitle>
                    <AlertDescription>
                        Please wait while we finalize the account linking.
                    </AlertDescription>
                </Alert>
            )}
            {successMsg && <Button onClick={handleClose}>Close Popup</Button>}
        </div>
    );
};

export default OAuthCallback;
